import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useCoreConfig } from '../../tarif-authoring/api/authoring';
import { asRecordArray, isRecord } from '../../tarif-authoring/model/asset-document';

/**
 * The lead lifecycle a version pins (SDD-013 §6), read-only.
 *
 * A version IS its manifest, and `refs.leadLifecycle` is one of its pins — so the machine a lead
 * on this version follows is shown here, beside the other artifacts, rather than one click away
 * in the asset editor. Statuses as chips (the tenant's name, the platform's kind), transitions as
 * a list, expiry as a sentence. Nothing here edits: the pinned asset is PUBLISHED and immutable,
 * and the next machine is a new asset pinned by a new version.
 *
 * A version that pins none follows the PLATFORM DEFAULT (§6.1), and the panel says so rather than
 * rendering nothing: "this version has no lifecycle" would be a false claim.
 */
export function PinnedLifecycle({
  slug,
  manifest,
}: {
  readonly slug: string;
  readonly manifest: unknown;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const ern = lifecycleRefOf(manifest);
  return (
    <Paper variant="outlined" component="section" aria-labelledby="pinned-lifecycle" sx={{ p: 2.5 }}>
      <Typography variant="h3" component="h2" id="pinned-lifecycle" sx={{ mb: 1.5 }}>
        {t('tarifs.lifecycle.title')}
      </Typography>
      {ern === undefined ? (
        <Alert severity="info">{t('tarifs.lifecycle.platformDefault')}</Alert>
      ) : (
        <PinnedLifecycleBody slug={slug} ern={ern} />
      )}
    </Paper>
  );
}

/** `refs.leadLifecycle` of a manifest, or undefined when the version pins none. */
export function lifecycleRefOf(manifest: unknown): string | undefined {
  if (!isRecord(manifest) || !isRecord(manifest.refs)) return undefined;
  const ern = manifest.refs.leadLifecycle;
  return typeof ern === 'string' && ern !== '' ? ern : undefined;
}

function PinnedLifecycleBody({ slug, ern }: { readonly slug: string; readonly ern: string }): ReactNode {
  const { t } = useTranslation('insurance');
  // The asset's name is the ERN's last segment: `tarifservice:fahrrad/lead_lifecycle.v1`.
  const name = ern.slice(ern.lastIndexOf('/') + 1);
  const asset = useCoreConfig(slug, name);

  if (asset.isPending) return <Skeleton variant="rounded" height={120} />;
  const config = asset.data?.coreConfig?.config;
  if (!isRecord(config)) {
    return <Alert severity="warning">{t('tarifs.lifecycle.unreadable', { ern })}</Alert>;
  }
  const statuses = asRecordArray(config.statuses);
  const transitions = asRecordArray(config.transitions);
  const expiry = isRecord(config.expiry) ? config.expiry : undefined;
  const nameOf = (code: unknown): string => {
    const status = statuses.find((candidate) => candidate.code === code);
    return typeof status?.name === 'string' ? status.name : String(code);
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
        {ern}
      </Typography>

      <Stack spacing={0.75}>
        <Typography variant="subtitle2" component="span" id="pinned-lifecycle-statuses">
          {t('tarifs.lifecycle.statuses')}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          role="list"
          aria-labelledby="pinned-lifecycle-statuses"
          sx={{ flexWrap: 'wrap' }}
        >
          {statuses.map((status, index) => {
            const code = typeof status.code === 'string' ? status.code : String(index);
            const kind = typeof status.kind === 'string' ? status.kind : '';
            const initial = config.initial === code;
            return (
              <Chip
                key={code}
                role="listitem"
                size="small"
                variant={initial ? 'filled' : 'outlined'}
                color={kind === 'CONVERTED' ? 'success' : kind === 'ACCEPTED' ? 'info' : 'default'}
                label={`${nameOf(code)} · ${kind}${status.locked === true ? ` · ${t('tarifs.lifecycle.locked')}` : ''}`}
              />
            );
          })}
        </Stack>
      </Stack>

      <Stack spacing={0.75}>
        <Typography variant="subtitle2" component="span" id="pinned-lifecycle-transitions">
          {t('tarifs.lifecycle.transitions')}
        </Typography>
        <List dense disablePadding aria-labelledby="pinned-lifecycle-transitions">
          {transitions.map((transition, index) => {
            const from = typeof transition.from === 'string' ? transition.from : '';
            const to = typeof transition.to === 'string' ? transition.to : '';
            const notes = [
              transition.actor === 'SYSTEM' ? t('tarifs.lifecycle.system') : undefined,
              transition.requires === 'approve' ? t('tarifs.lifecycle.requiresApprove') : undefined,
              transition.fourEyes === true ? t('tarifs.lifecycle.fourEyes') : undefined,
              transition.reasonRequired === true ? t('tarifs.lifecycle.reasonRequired') : undefined,
            ].filter((note): note is string => note !== undefined);
            return (
              <ListItem key={`${from}-${to}-${String(index)}`} disableGutters>
                <ListItemText
                  primary={`${nameOf(from)} → ${nameOf(to)}`}
                  secondary={notes.length === 0 ? undefined : notes.join(' · ')}
                />
              </ListItem>
            );
          })}
        </List>
      </Stack>

      {expiry !== undefined && (
        <Typography variant="body2">
          {t('tarifs.lifecycle.expiry', {
            days: typeof expiry.afterDays === 'number' ? expiry.afterDays : '—',
            from: (Array.isArray(expiry.from) ? expiry.from : []).map(nameOf).join(', '),
            to: nameOf(expiry.to),
          })}
        </Typography>
      )}
    </Stack>
  );
}
